import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { FeiVO as BaseFeiVO, FeiKeyDef, VisionVO } from '@wizeoo/akm-compute-lib';
import { Baseline, BaselineValue, FormulaEngineInstance, MeasureModeItem, VisionStep, VisionStepPerimeterKey } from '@wizeoo/akm-message-lib';
import { ViewData } from '../../model/view.data';
import { VisionStepPerimeterVO } from '../../vo/vision/visionstepperimeter.vo';
import { Model } from '../../model/model';
import { WidgetLine } from '@wizeoo/akm-compute-lib';
// import { LFStructuralRender } from '../../manager/workingdata/listfield.engine';
import { PermissionEngine } from '../../manager/permission/permission.engine';
import { IWidgetFilterItem } from '../../manager/widgets/i-widget';

/**
 * transmission d'un événement trigger depuis wizCompute
 */
export interface TriggerData {
  mmi: MeasureModeItem; // mmi qui porte le calcul
  line: WidgetLine; // ligne cible
  fei: FeiVO; // porteur
}

/**
 * encapsulation d'un FormulaEngineInstance.
 * Représente les calculs pour 1 niveau de step
 */
export class FeiVO extends BaseFeiVO {

  public get model(): Model {
    return this._model as Model;
  }
  public set model(value: Model) {
      this._model = value;
  }
  
  /** constructeur */
  constructor(pSource: FormulaEngineInstance, model: Model, pVision: VisionVO, pStep: VisionStep, pScen:string) {
    super(pSource, model, pVision, pStep, pScen);
    this.permissionMap = model.permissionEngine.getFeiPermissions(this);
  }

  /** le vsp pour manipulation dans la vue de génération des lignes */
  public visionStepPerimeterVO: VisionStepPerimeterVO = null;

  /** la map des permissions sur le step / le(s) stepPerimeters codeStep||codeStepPerimeter = int(<=15) (1=C, 2=R, 4=U, 8=D) *-1 = doNone */
  public permissionMap: Map<string, number> = new Map<string, number>();

  /** les clés de stepPerimeters (si portée vsp) */
  public vspKeysMap: Map<string, VisionStepPerimeterKey> = new Map<string, VisionStepPerimeterKey>();

  /** mémorisation des structures de rendus pour les mesures de type LIST clé mesure = map */
  public listMeasureRenderMap: Map<string, Map<string, IWidgetFilterItem>> = null;
  // public listMeasureRenderMap: Map<string, Map<number, LFStructuralRender>> = null;

  // private _meMeasure: FeiKeyDef = null;
  // private _meChecked: boolean = false;
  // public getMeMeasure(): FeiKeyDef {
  //   let result: FeiKeyDef = null;
  //   if(!this._meChecked) {
  //     this.keyMap.forEach()
  //   }

  //   return result;
  // }

  /* ##############################################################################
          autorisations sur les FEIs / les widgets selon vs / vsp
  ############################################################################## */

  /**
   * demande de suppression d'une WidgetLine (un pool possible, si pivot)
   * @param wls widgetLines sources
   * @returns true si tout est supprimable
   */
  public canDeleteItem(wls: WidgetLine[], silent: boolean = false): boolean {
    return this.canEngine(wls, PermissionEngine.BIN_DELETE, silent);
  }

  /**
   * demande de modif d'une WidgetLine (un pool possible, si pivot)
   * @param wls sources
   * @returns true si tout est modifiable
   */
  public canUpdateItem(wls: WidgetLine[], silent: boolean = false): boolean {
    return this.canEngine(wls, PermissionEngine.BIN_UPDATE, silent);
  }

  /**
   * demande de créa d'une WidgetLine
   * @param wls sources
   * @returns true si tout est modifiable
   */
  public canCreateItem(wls: WidgetLine[], silent: boolean = false): boolean {
    return this.canEngine(wls, PermissionEngine.BIN_CREATE, silent);
  }

  /**
   * demande d'altération d'une WidgetLine (un pool possible, si pivot)
   * @param wls sources
   * @returns true si tout est altérable
   */
  private canEngine(wls: WidgetLine[], kind: number, silent: boolean = false): boolean {
    let result = false;
    // console.log('fei canEngine', kind);
    if (Model.getInstance().permissionEngine.feiBasePermission(this, kind)) {
      const baseCode: string = this.visionCode + ViewData.KEY_SEPARATOR + this.visionStepLevel;
      const score: number = this.permissionMap.get(baseCode);
      // console.log('fei canEngine score', score, this.permissionMap);
      if (WizMathUtils.wizBitwize(score, kind)) { // j'ai le droit de suppression au step, OK
        result = true;
      } else { // je vérifie que j'ai le droit pour TOUTES les baselines
        let allOk: boolean = true;
        let vspkey: VisionStepPerimeterKey = null;
        wls.forEach((wl: WidgetLine) => {
          vspkey = this.vspKeysMap.get(wl.bl.key);
          if (!vspkey || !WizMathUtils.wizBitwize(this.permissionMap.get(baseCode + ViewData.KEY_SEPARATOR + vspkey.perimeterCode), kind)) {
            allOk = false;
          }
          // console.log('vspkey to CRUD',pl.witnessBaseline.key, vspkey, allOk);
        });

        if (allOk) { // tous ok
          result = true;
        }
      }
    }

    if (!result && !silent) {
      WizPopUp.toast('$.common.lack.rights.$', WizIcon.TOAST_WARNING).show();
    }
    return result;
  }

  /**
   * mise à jour de la baseline sur retour de service (pour la version)
   * @param bl: la baseline sur retour de service
   */
  public updateBaseline(bl: Baseline, exist: Baseline): void {
    if (exist) {
      exist.version = bl.version;
      exist.updateBy = bl.updateBy;
      exist.updateTime = bl.updateTime;
      exist.revisionNumber = bl.revisionNumber;
      // console.log('ici rebuild baseline ??', bl.fkeyjson, exist.fkeyjson);
      exist.fkeyjson = bl.fkeyjson;
      // BaselineEngine.buildTrio(this, exist);
    }
  }

  /**
   * mise à jour de la baseline sur retour de service (gestion BLV)
   * @param blv: la baselineValue sur retour de service
   */
  public updateBaselineValue(blv: BaselineValue, baseline: Baseline, doAdd: boolean = true): void {
    if (baseline) {
      if(!baseline.values) {
        baseline.values = {};
      }
      baseline.values[blv.mkey] = blv;
      if(doAdd) { // c'est une ligne de scenar Max, on met à jour la matrice
        // console.log('updateBaselineValue', this.uniqueName, baseline.key, blv.mkey, blv.valor);
        this.model.cellMatrixEngine.addCell(baseline.level, baseline.key, baseline.scen, blv.mkey, blv.valor);
      }
      
    }
  }
}
